/**
 * Navigation Component
 *
 * Sidebar navigation for the Thank You admin interface.
 *
 * @since 1.0.0
 */

import { Cog8ToothIcon, QueueListIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Button } from '@redfoxwp/core-components/components/button';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { Modal, type ModalStrings } from '@redfoxwp/core-components/components/modal';
import { applyFilters } from '@wordpress/hooks';
import { __ } from '@wordpress/i18n';
import { useState } from 'react';
import { useHashLocation } from 'wouter/use-hash-location';

import { useNavigationGuard } from '../../hooks';
import {
	NavigationItem,
	NavigationItemIcon,
	NavigationItemLabel,
	NavigationMenu,
} from './styled';
import type { NavigationItemData,ProtectedNavigationItemProps } from './types';

/**
 * Protected Navigation Item component that checks for unsaved changes
 *
 * @since 1.0.0
 */
const ProtectedNavigationItem = ({
	path,
	label,
	description,
	icon,
}: ProtectedNavigationItemProps): JSX.Element => {
	const [location, setLocation] = useHashLocation();
	const { hasUnsavedChanges } = useNavigationGuard();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [pendingPath, setPendingPath] = useState<string | null>(null);

	// Get the list of protected paths that should show confirmation modal
	// when navigating away with unsaved changes
	const protectedPaths = applyFilters(
		'redfox_thank_you_protected_paths',
		['/settings'],
	) as string[];

	// Check if the current location matches the path or starts with the path (for submenu items)
	// For root path ('/'), only match exactly to avoid matching all paths
	const isActive = path === '/'
		? location === path
		: location === path || location.startsWith(`${path}/`);

	// Check if the current location is a protected path
	const isOnProtectedPath = protectedPaths.some(
		(protectedPath) => location === protectedPath || location.startsWith(`${protectedPath}/`),
	);

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		// If we're already on this path, don't do anything
		if (location === path) {
			return;
		}

		// Only show confirmation modal if:
		// 1. There are unsaved changes AND
		// 2. User is currently on a protected path
		if (hasUnsavedChanges && isOnProtectedPath) {
			event.preventDefault();
			setPendingPath(path);
			setShowConfirmModal(true);
		}
	};

	const handleConfirmNavigation = () => {
		if (pendingPath) {
			setLocation(pendingPath);
			setPendingPath(null);
		}
		setShowConfirmModal(false);
	};

	const handleCancelNavigation = () => {
		setPendingPath(null);
		setShowConfirmModal(false);
	};

	// Create localized strings for the modal
	const modalStrings: ModalStrings = {
		closeModal: __('Close modal', 'redfox-thank-you'),
	};

	return (
		<>
			<NavigationItem to={path} title={description} onClick={handleClick} $isActive={isActive}>
				<NavigationItemIcon>{icon}</NavigationItemIcon>
				<NavigationItemLabel>{label}</NavigationItemLabel>
			</NavigationItem>

			<Modal
				isOpen={showConfirmModal}
				onClose={handleCancelNavigation}
				title={__('Unsaved Changes', 'redfox-thank-you')}
				strings={modalStrings}
				footer={
					<Flex gap='10px' justify='flex-end'>
						<Button variant='outline' onClick={handleCancelNavigation}>
							{__('Cancel', 'redfox-thank-you')}
						</Button>
						<Button variant='solid' onClick={handleConfirmNavigation}>
							{__('Leave Page', 'redfox-thank-you')}
						</Button>
					</Flex>
				}
			>
				<p>
					{__(
						'You have unsaved changes. If you leave now, your changes will be lost. Are you sure you want to continue?',
						'redfox-thank-you',
					)}
				</p>
			</Modal>
		</>
	);
};

/**
 * Navigation component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const Navigation = (): JSX.Element => {
	// Apply filter to allow hiding the Pro Features navigation item (hidden when Pro is active)
	const showProFeatures = applyFilters('redfox_thank_you_show_pro_features', true) as boolean;

	const baseNavigationItems: NavigationItemData[] = [
		{
			path: '/',
			label: __('Pages', 'redfox-thank-you'),
			description: __('Manage thank you pages', 'redfox-thank-you'),
			icon: <QueueListIcon width={20} height={20} />,
		},
		{
			path: '/settings',
			label: __('Settings', 'redfox-thank-you'),
			description: __('Configure settings', 'redfox-thank-you'),
			icon: <Cog8ToothIcon width={20} height={20} />,
		},
	];

	// Only add Pro Features navigation item if Pro is not active
	if (showProFeatures) {
		baseNavigationItems.push({
			path: '/pro-features',
			label: __('Pro Features', 'redfox-thank-you'),
			description: __('Explore premium features', 'redfox-thank-you'),
			icon: <SparklesIcon width={20} height={20} />,
		});
	}

	const navigationItems = applyFilters(
		'redfox_thank_you_navigation_items',
		baseNavigationItems,
	) as NavigationItemData[];

	return (
		<NavigationMenu>
			{navigationItems.map((item) => (
				<ProtectedNavigationItem
					key={item.path}
					path={item.path}
					label={item.label}
					description={item.description}
					icon={item.icon}
				/>
			))}
		</NavigationMenu>
	);
};
